'use client';

import React from 'react';

import { useRouter } from 'next/navigation';
import { useDispatch } from 'react-redux';

import { signIn } from 'next-auth/react';

import { Loader2 } from 'lucide-react';

import { useForm } from 'react-hook-form';

import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { toast } from 'sonner';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import { InputOTP, InputOTPSeparator, InputOTPSlot } from '../ui/input-otp';

import { updateUser } from '@/redux/features/user-slice';

const FormSchema = z.object({
  pin: z.string().min(6, {
    message: 'Your one-time password must be 6 characters.',
  }),
});

function OtpInput({ phoneNumber }: { phoneNumber: number }) {
  const router = useRouter();
  const dispatch = useDispatch();

  const [isError, setIsError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pin: '',
    },
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    setIsLoading(true);
    setIsError(false);

    try {
      const response = await signIn('credentials', {
        otp: data.pin,
        phone: `91${phoneNumber}`,
        redirect: false,
      });

      if (response?.error) {
        toast.error(response.error);
        return;
      }

      if (response?.ok) {
        toast.success('Successfully logged in!');
        window.location.href = '/';
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='w-[100%] space-y-4'
      >
        <FormField
          control={form.control}
          name='pin'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-text'>One-Time Password</FormLabel>
              <FormControl>
                <InputOTP maxLength={6} {...field}>
                  <div className='flex w-[100%] items-center justify-between'>
                    {Array.from({ length: 3 }).map((_, index) => (
                      <InputOTPSlot
                        key={index}
                        index={index}
                        className='w-[50px] border-[1px] border-gray-300 text-text outline-1 outline-background'
                        data-testid='textbox'
                      />
                    ))}

                    <InputOTPSeparator
                      data-testid='otp-separator'
                      className='text-text'
                    />

                    {Array.from({ length: 3 }).map((_, index) => (
                      <InputOTPSlot
                        key={index + 3}
                        index={index + 3}
                        className='w-[50px] border-[1px] border-gray-300 text-text outline-1 outline-background'
                        data-testid='textbox'
                      />
                    ))}
                  </div>
                </InputOTP>
              </FormControl>
              <FormDescription className='text-text'>
                Please enter the one-time password sent to your phone.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <button
          type='submit'
          disabled={isLoading}
          className='w-full flex justify-center py-3 px-4 rounded-md shadow-md text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200'
        >
          {isLoading ? (
            <Loader2 className='h-5 w-5 animate-spin' />
          ) : (
            'Login'
          )}
        </button>
      </form>
    </Form>
  );
}

export default OtpInput;
